import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider } from './contexts/ThemeContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import EntryExitConfigPage from './pages/EntryExitPage';
import BseEntryExitPage from './pages/BseEntryExitPage';
import BseOrdersPage from './pages/BseOrdersPage';
import FilterSettingsPage from './pages/FilterSettingsPage';
import MarketDataPage from './pages/MarketDataPage';
import LivePage from './pages/LivePage';
import SettingsPage from './pages/SettingsPage';
import BrokerManagementPage from './pages/BrokerManagementPage';
import OrdersPage from './pages/OrdersPage';
import SpreadEntryExitPage from './pages/SpreadEntryExitPage';
import ManualSpreadPage from './pages/ManualSpreadPage';
import SpreadOrdersPage from './pages/SpreadOrdersPage';
import OptionSellingPage from './pages/OptionSellingPage';
import OptionSellingOrdersPage from './pages/OptionSellingOrdersPage';
import CashMarketPage from './pages/CashMarketPage';
import NiftyBuyingPage from './pages/NiftyBuyingPage';
import NiftyBuyingOrdersPage from './pages/NiftyBuyingOrdersPage';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center">
          <div className="spinner-border text-light" style={{ width: '4rem', height: '4rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-3 text-white fw-bold">Loading...</div>
        </div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center">
          <div className="spinner-border text-light" style={{ width: '4rem', height: '4rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-3 text-white fw-bold">Loading...</div>
        </div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" /> : <>{children}</>;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
        <div className="App">
          <Routes>
            {/* <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            /> */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/entry-exit"
              element={
                <ProtectedRoute>
                  <EntryExitConfigPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/filter-settings"
              element={
                <ProtectedRoute>
                  <FilterSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-data"
              element={
                <ProtectedRoute>
                  <MarketDataPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-page"
              element={
                <ProtectedRoute>
                  <LivePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broker-management"
              element={
                <ProtectedRoute>
                  <BrokerManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <OrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bse/entry-exit"
              element={
                <ProtectedRoute>
                  <BseEntryExitPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bse/orders"
              element={
                <ProtectedRoute>
                  <BseOrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manual-spread"
              element={
                <ProtectedRoute>
                  <ManualSpreadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ny-spread"
              element={
                <ProtectedRoute>
                  <SpreadEntryExitPage market="NIFTY" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sx-spread"
              element={
                <ProtectedRoute>
                  <SpreadEntryExitPage market="SENSEX" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ny-spread/orders"
              element={
                <ProtectedRoute>
                  <SpreadOrdersPage market="NIFTY" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sx-spread/orders"
              element={
                <ProtectedRoute>
                  <SpreadOrdersPage market="SENSEX" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/option-selling"
              element={
                <ProtectedRoute>
                  <OptionSellingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/option-selling/orders"
              element={
                <ProtectedRoute>
                  <OptionSellingOrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cash-market"
              element={
                <ProtectedRoute>
                  <CashMarketPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/nifty-buying"
              element={
                <ProtectedRoute>
                  <NiftyBuyingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/nifty-buying/orders"
              element={
                <ProtectedRoute>
                  <NiftyBuyingOrdersPage />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </div>
      </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;